/**
 * Workload domain event schemas.
 *
 * Typed payload validation for all workload-related Mycelium events.
 * Rule: fields added after initial release MUST be `.optional()`.
 *
 * @module workload.events
 */

import { z } from 'zod'
import { BackboneEventSchema } from '../types/backbone.ts'

// ── Shared payload schema ───────────────────────────────────────────────

const WorkloadPayload = z.object({
  userId: z.string(),
  date: z.string().optional(),
  plannedHours: z.number().optional(),
  actualHours: z.number().optional(),
  taskCount: z.number().optional(),
  completedTasks: z.number().optional(),
  focusTimeMinutes: z.number().optional(),
  interruptionCount: z.number().optional(),
  productivityScore: z.number().optional(),
  capacityUtilization: z.number().optional(),
  trend: z.string().optional(),
  source: z.string(),
})

// ── Event schemas ───────────────────────────────────────────────────────

export const WorkloadCapacityUpdatedEvent = BackboneEventSchema.extend({
  domain: z.literal('workload'),
  type: z.literal('workload_capacity_updated'),
  payload: WorkloadPayload,
})

export const WorkloadThresholdExceededEvent = BackboneEventSchema.extend({
  domain: z.literal('workload'),
  type: z.literal('workload_threshold_exceeded'),
  payload: WorkloadPayload,
})

export const WorkloadAnalyticsUpdatedEvent = BackboneEventSchema.extend({
  domain: z.literal('workload'),
  type: z.literal('workload_analytics_updated'),
  payload: WorkloadPayload,
})

export const WorkloadTrendDetectedEvent = BackboneEventSchema.extend({
  domain: z.literal('workload'),
  type: z.literal('workload_trend_detected'),
  payload: WorkloadPayload,
})

export const ProductivityScoredEvent = BackboneEventSchema.extend({
  domain: z.literal('workload'),
  type: z.literal('productivity_scored'),
  payload: WorkloadPayload,
})

// ── All workload events ─────────────────────────────────────────────────

export const WorkloadDomainEvents = [
  WorkloadCapacityUpdatedEvent,
  WorkloadThresholdExceededEvent,
  WorkloadAnalyticsUpdatedEvent,
  WorkloadTrendDetectedEvent,
  ProductivityScoredEvent,
] as const
